import React, { useState } from "react";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from ".././Helmet";
import RelatedLinks from "../RelatedLinks";
import sphereimg from "../../../images/ball.png";
import coneimg from "../../../images/cone.png";
import cube from "../../../images/cube.png";
import cylinderimg from "../../../images/cylinder.png";
import rectangularimg from "../../../images/prism.png";
import capsuleimg from "../../../images/capsule.png";
import conicalimg from "../../../images/conical-frustum.png";
import ellipsoidimg from "../../../images/ellipsoid.png";
import pyramidimg from "../../../images/square-pyramid.png";
import tubeimg from "../../../images/tube.png";

const TITLE = "Calculatrice de volumes des formes géométriques";
const DESCRIPTION =
  "Calculatrice gratuite pour calculer le volume des formes géométriques courantes : sphère, cône, cube, cylindre, capsule, tronc de cône, ellipsoïde, pyramide, tube et réservoir rectangulaire.";
const KEYWORDS =
  "calculatrice volume, sphère, cône, cube, cylindre, capsule, tronc de cône, ellipsoïde, pyramide, tube, formes géométriques, calcul volume";
const OG_URL = "https://do-calculate.com/calculator/fr/volumes/";

const conversionMap = {
  centimètre: 0.01,
  pouce: 0.0254,
  mètre: 1,
  kilomètre: 1000,
  pied: 0.3048,
  mile: 1609.34,
};

const UNITS = ["centimètre", "pouce", "mètre", "kilomètre", "pied", "mile"];

function convertToBase(value, unit) {
  return value * conversionMap[unit];
}

function dominantUnit(units) {
  return units.reduce((a, b) => (conversionMap[a] >= conversionMap[b] ? a : b));
}

const shapes = [
  { key: "sphere",      label: "Sphère",                 img: sphereimg },
  { key: "cone",        label: "Cône",                   img: coneimg },
  { key: "cube",        label: "Cube",                   img: cube },
  { key: "cylinder",    label: "Cylindre",               img: cylinderimg },
  { key: "rectangular", label: "Réservoir rectangulaire",img: rectangularimg },
  { key: "capsule",     label: "Capsule",                img: capsuleimg },
  { key: "conical",     label: "Tronc de cône",          img: conicalimg },
  { key: "ellipsoid",   label: "Ellipsoïde",             img: ellipsoidimg },
  { key: "pyramid",     label: "Pyramide carrée",        img: pyramidimg },
  { key: "tube",        label: "Tube",                   img: tubeimg },
];

const fieldDefs = {
  sphere:      [{ key: "radius", label: "Rayon" }],
  cone:        [{ key: "baseRadius", label: "Rayon de la base" }, { key: "height", label: "Hauteur" }],
  cube:        [{ key: "edge", label: "Arête" }],
  cylinder:    [{ key: "baseRadius", label: "Rayon de la base" }, { key: "height", label: "Hauteur" }],
  rectangular: [{ key: "length", label: "Longueur" }, { key: "width", label: "Largeur" }, { key: "height", label: "Hauteur" }],
  capsule:     [{ key: "baseRadius", label: "Rayon" }, { key: "height", label: "Hauteur du cylindre" }],
  conical:     [{ key: "topRadius", label: "Rayon supérieur" }, { key: "bottomRadius", label: "Rayon inférieur" }, { key: "height", label: "Hauteur" }],
  ellipsoid:   [{ key: "axis1", label: "Demi-axe a" }, { key: "axis2", label: "Demi-axe b" }, { key: "axis3", label: "Demi-axe c" }],
  pyramid:     [{ key: "baseEdge", label: "Côté de la base" }, { key: "height", label: "Hauteur" }],
  tube:        [{ key: "outerDiameter", label: "Diamètre extérieur" }, { key: "innerDiameter", label: "Diamètre intérieur" }, { key: "length", label: "Longueur" }],
};

const formulas = {
  sphere:      "V = (4/3) × π × r³",
  cone:        "V = (1/3) × π × r² × h",
  cube:        "V = a³",
  cylinder:    "V = π × r² × h",
  rectangular: "V = longueur × largeur × hauteur",
  capsule:     "V = π × r² × ((4/3) × r + h)",
  conical:     "V = (1/3) × π × h × (r₁² + r₁r₂ + r₂²)",
  ellipsoid:   "V = (4/3) × π × a × b × c",
  pyramid:     "V = (1/3) × a² × h",
  tube:        "V = π × (d₁² − d₂²) / 4 × l",
};

function calcVolume(shape, vals, units) {
  const get = (key) => convertToBase(parseFloat(vals[key]), units[key] || "mètre");
  switch (shape) {
    case "sphere": {
      const r = get("radius");
      return { vol: (4 / 3) * Math.PI * Math.pow(r, 3), unit: units["radius"] || "mètre" };
    }
    case "cone": {
      const ru = units["baseRadius"] || "mètre";
      const hu = units["height"] || "mètre";
      const resUnit = dominantUnit([ru, hu]);
      const r = convertToBase(parseFloat(vals["baseRadius"]), ru) / conversionMap[resUnit];
      const h = convertToBase(parseFloat(vals["height"]), hu) / conversionMap[resUnit];
      return { vol: (1 / 3) * Math.PI * r * r * h, unit: resUnit };
    }
    case "cube": {
      const a = get("edge");
      return { vol: Math.pow(a, 3), unit: units["edge"] || "mètre" };
    }
    case "cylinder": {
      const ru = units["baseRadius"] || "mètre";
      const hu = units["height"] || "mètre";
      const resUnit = dominantUnit([ru, hu]);
      const r = convertToBase(parseFloat(vals["baseRadius"]), ru) / conversionMap[resUnit];
      const h = convertToBase(parseFloat(vals["height"]), hu) / conversionMap[resUnit];
      return { vol: Math.PI * r * r * h, unit: resUnit };
    }
    case "rectangular": {
      const lu = units["length"] || "mètre";
      const wu = units["width"] || "mètre";
      const hu = units["height"] || "mètre";
      const resUnit = dominantUnit([lu, wu, hu]);
      const l = convertToBase(parseFloat(vals["length"]), lu) / conversionMap[resUnit];
      const w = convertToBase(parseFloat(vals["width"]), wu) / conversionMap[resUnit];
      const h = convertToBase(parseFloat(vals["height"]), hu) / conversionMap[resUnit];
      return { vol: l * w * h, unit: resUnit };
    }
    case "capsule": {
      const ru = units["baseRadius"] || "mètre";
      const hu = units["height"] || "mètre";
      const resUnit = dominantUnit([ru, hu]);
      const r = convertToBase(parseFloat(vals["baseRadius"]), ru) / conversionMap[resUnit];
      const h = convertToBase(parseFloat(vals["height"]), hu) / conversionMap[resUnit];
      return { vol: (4 / 3) * Math.PI * Math.pow(r, 3) + Math.PI * r * r * h, unit: resUnit };
    }
    case "conical": {
      const t = units["topRadius"] || "mètre";
      const b = units["bottomRadius"] || "mètre";
      const hu = units["height"] || "mètre";
      const resUnit = dominantUnit([t, b, hu]);
      const r1 = convertToBase(parseFloat(vals["topRadius"]), t) / conversionMap[resUnit];
      const r2 = convertToBase(parseFloat(vals["bottomRadius"]), b) / conversionMap[resUnit];
      const h = convertToBase(parseFloat(vals["height"]), hu) / conversionMap[resUnit];
      return { vol: (1 / 3) * Math.PI * h * (r1 * r1 + r1 * r2 + r2 * r2), unit: resUnit };
    }
    case "ellipsoid": {
      const u1 = units["axis1"] || "mètre";
      const u2 = units["axis2"] || "mètre";
      const u3 = units["axis3"] || "mètre";
      const resUnit = dominantUnit([u1, u2, u3]);
      const a = convertToBase(parseFloat(vals["axis1"]), u1) / conversionMap[resUnit];
      const bv = convertToBase(parseFloat(vals["axis2"]), u2) / conversionMap[resUnit];
      const c = convertToBase(parseFloat(vals["axis3"]), u3) / conversionMap[resUnit];
      return { vol: (4 / 3) * Math.PI * a * bv * c, unit: resUnit };
    }
    case "pyramid": {
      const eu = units["baseEdge"] || "mètre";
      const hu = units["height"] || "mètre";
      const resUnit = dominantUnit([eu, hu]);
      const a = convertToBase(parseFloat(vals["baseEdge"]), eu) / conversionMap[resUnit];
      const h = convertToBase(parseFloat(vals["height"]), hu) / conversionMap[resUnit];
      return { vol: (1 / 3) * a * a * h, unit: resUnit };
    }
    case "tube": {
      const ou = units["outerDiameter"] || "mètre";
      const iu = units["innerDiameter"] || "mètre";
      const lu = units["length"] || "mètre";
      const resUnit = dominantUnit([ou, iu, lu]);
      const d1 = convertToBase(parseFloat(vals["outerDiameter"]), ou) / conversionMap[resUnit];
      const d2 = convertToBase(parseFloat(vals["innerDiameter"]), iu) / conversionMap[resUnit];
      const l = convertToBase(parseFloat(vals["length"]), lu) / conversionMap[resUnit];
      if (d2 >= d1) return null;
      return { vol: Math.PI * (d1 * d1 - d2 * d2) / 4 * l, unit: resUnit };
    }
    default: return null;
  }
}

function VolumesCalculator() {
  const [shape, setShape] = useState("sphere");
  const [vals, setVals] = useState({});
  const [units, setUnits] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleValChange = (k, v) => setVals((p) => ({ ...p, [k]: v }));
  const handleUnitChange = (k, v) => setUnits((p) => ({ ...p, [k]: v }));

  const handleCalculate = () => {
    const fds = fieldDefs[shape];
    for (const f of fds) {
      const v = parseFloat(vals[f.key]);
      if (!vals[f.key] || isNaN(v) || v < 0) {
        setError(`Veuillez entrer une valeur valide et positive pour : ${f.label}`);
        setResult(null);
        return;
      }
    }
    if (shape === "tube") {
      const ou = units["outerDiameter"] || "mètre";
      const iu = units["innerDiameter"] || "mètre";
      const resUnit = dominantUnit([ou, iu]);
      const d1 = convertToBase(parseFloat(vals["outerDiameter"]), ou) / conversionMap[resUnit];
      const d2 = convertToBase(parseFloat(vals["innerDiameter"]), iu) / conversionMap[resUnit];
      if (d2 >= d1) {
        setError("Le diamètre intérieur doit être inférieur au diamètre extérieur");
        setResult(null);
        return;
      }
    }
    const res = calcVolume(shape, vals, units);
    if (!res || isNaN(res.vol) || res.vol < 0) {
      setError("Les valeurs saisies ne permettent pas de calculer un volume valide.");
      setResult(null);
    } else {
      setError("");
      setResult(res);
    }
  };

  const handleShapeChange = (s) => { setShape(s); setVals({}); setUnits({}); setResult(null); setError(""); };

  const currentShape = shapes.find((s) => s.key === shape);

  return (
    <div className="ltr">
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} />

      <div className="row"><div className="col-12 col-md-8"><FirstAds /></div></div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>Cette calculatrice calcule le volume des formes géométriques courantes : sphère, cône, cube, cylindre, réservoir rectangulaire, capsule, tronc de cône, ellipsoïde, pyramide et tube, avec conversion d'unités.</p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />

                {/* Shape selector */}
                <div className="form-group">
                  <label><b>Sélectionner la forme :</b></label>
                  <select className="form-control" value={shape} onChange={(e) => handleShapeChange(e.target.value)}>
                    {shapes.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>

                {currentShape && (
                  <div style={{ textAlign: "center", margin: "10px 0" }}>
                    <img src={currentShape.img} alt={currentShape.label} style={{ maxHeight: "80px" }} />
                  </div>
                )}

                <div className="alert alert-info" style={{ fontSize: "13px" }}>
                  <b>Formule :</b> {formulas[shape]}
                </div>

                {fieldDefs[shape].map((f) => (
                  <div className="form-group" key={f.key} style={{ marginBottom: "10px" }}>
                    <label>{f.label} :</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        placeholder={`Entrez ${f.label}`}
                        value={vals[f.key] || ""}
                        onChange={(e) => handleValChange(f.key, e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <select
                        className="form-control"
                        style={{ width: "140px" }}
                        value={units[f.key] || "mètre"}
                        onChange={(e) => handleUnitChange(f.key, e.target.value)}
                      >
                        {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  </div>
                ))}

                <button className="btn btn-success" onClick={handleCalculate}>Calculer le volume</button>

                {error && <p className="p-3 mb-2 bg-warning text-dark mt-2">{error}</p>}
                {result !== null && (
                  <div className="p-3 mb-2 bg-success text-white mt-2">
                    <b>Volume = {result.vol.toLocaleString("fr-FR", { maximumFractionDigits: 6 })} {result.unit}³</b>
                  </div>
                )}
              </div>

              <div className="col">
                <SecondAds />
                <MainCalculatorsList />
              </div>
            </div>

            <hr />
            <div className="row">
              <RelatedLinks />
              <hr style={{ width: "100%" }} />
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 col-md-8">
        <div className="info-sections-container">
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume des formes géométriques</h3></div>
            <div className="info-section-body">
              <p>Le volume est la mesure de l'espace tridimensionnel occupé par un objet. L'unité SI du volume est le mètre cube (m³). Cette calculatrice prend en charge la conversion d'unités : centimètre, pouce, mètre, kilomètre, pied et mile.</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'une sphère</h3></div>
            <div className="info-section-body">
              <p>La sphère est un corps dont tous les points sont équidistants du centre.</p>
              <p><strong>V = (4/3) × π × r³</strong></p>
              <p>Exemple : r = 5 m → V = (4/3) × π × 125 ≈ 523,60 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'un cône</h3></div>
            <div className="info-section-body">
              <p>Un cône a une base circulaire et se rétrécit vers un sommet.</p>
              <p><strong>V = (1/3) × π × r² × h</strong></p>
              <p>Exemple : r = 3 m, h = 5 m → V = (1/3) × π × 9 × 5 ≈ 47,12 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'un cube</h3></div>
            <div className="info-section-body">
              <p>Un cube est délimité par six faces carrées identiques.</p>
              <p><strong>V = a³</strong></p>
              <p>Exemple : a = 3 m → V = 27 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'un cylindre</h3></div>
            <div className="info-section-body">
              <p>Un cylindre possède deux bases circulaires reliées par une surface latérale.</p>
              <p><strong>V = π × r² × h</strong></p>
              <p>Exemple : r = 3 m, h = 4 m → V = π × 9 × 4 ≈ 113,10 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'un réservoir rectangulaire</h3></div>
            <div className="info-section-body">
              <p>Un parallélépipède rectangle avec des côtés de longueurs différentes.</p>
              <p><strong>V = longueur × largeur × hauteur</strong></p>
              <p>Exemple : 4 × 3 × 2 = 24 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'une capsule</h3></div>
            <div className="info-section-body">
              <p>Une capsule combine un cylindre et deux hémisphères.</p>
              <p><strong>V = π × r² × ((4/3) × r + h)</strong></p>
              <p>Exemple : r = 1,5 m, h = 3 m → V ≈ 35,34 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'un tronc de cône</h3></div>
            <div className="info-section-body">
              <p>Partie d'un cône comprise entre deux plans parallèles.</p>
              <p><strong>V = (1/3) × π × h × (r₁² + r₁r₂ + r₂²)</strong></p>
              <p>Exemple : r₁ = 0,3 m, r₂ = 1,8 m, h = 4 m → V ≈ 12,55 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'un ellipsoïde</h3></div>
            <div className="info-section-body">
              <p>Déformation d'une sphère selon trois axes perpendiculaires.</p>
              <p><strong>V = (4/3) × π × a × b × c</strong></p>
              <p>Exemple : a = 2,5, b = 3, c = 6 → V ≈ 188,50 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'une pyramide carrée</h3></div>
            <div className="info-section-body">
              <p>Pyramide à base carrée.</p>
              <p><strong>V = (1/3) × a² × h</strong></p>
              <p>Exemple : a = 5 m, h = 12 m → V = (1/3) × 25 × 12 = 100 m³</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Volume d'un tube</h3></div>
            <div className="info-section-body">
              <p>Cylindre creux défini par ses diamètres extérieur et intérieur.</p>
              <p><strong>V = π × (d₁² − d₂²) / 4 × l</strong></p>
              <p>Exemple : d₁ = 3 m, d₂ = 2,5 m, l = 10 m → V ≈ 21,60 m³</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VolumesCalculator;
